import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	doNothing,
	Else,
	If,
	Image,
	makeClassName,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { useHistory } from "@hex-insights/router";
import {
	permissions as allPermissions,
	Icon,
	StaticURLs,
	useElementSize,
	useReEnrollmentNoticeQuery,
} from "@hex-insights/verita-romania.shared";
import { reEnrollmentPageInfo } from "../../ReEnrollment/pageinfo";
import { getReEnrollmentContractInStudentFilter, getReEnrollmentStudentFilter } from "../../ReEnrollment/Utilities";
import styles from "./styles.module.css";

const requiredPermissions: string[] = [];

export type ReEnrollmentNoticePermissionRequiredProps = Partial<ChildrenProps>;

export function ReEnrollmentNoticePermissionRequired({ children }: ReEnrollmentNoticePermissionRequiredProps) {
	return (
		<PermissionRequired
			requiredPermissions={requiredPermissions}
			customCheck={checkReEnrollmentPermissions}
			checkMethod="and"
			customCheckMethod="and"
		>
			{children}
		</PermissionRequired>
	);
}

const requiredPermissionActions = [
	// People & Contact
	allPermissions.Person.View,
	allPermissions.Guardian.View,
	allPermissions.PhoneNumber.View,
	allPermissions.EmailAddress.View,
	allPermissions.Address.View,
	allPermissions.Student.View,
	// Medical
	allPermissions.MedicalCondition.View,
	allPermissions.Medication.View,
	allPermissions.Vaccine.View,
	allPermissions.StudentMedicalCondition.View,
	allPermissions.StudentOTCMedication.View,
	allPermissions.StudentPrescriptionMedication.View,
	allPermissions.StudentVaccinationRecord.View,
	// Data Consent
	allPermissions.StudentDataConsent.View,
	allPermissions.StudentDataConsent.Create,
	allPermissions.StudentDataConsent.Update,
	// Financials
	allPermissions.Contract.View,
	allPermissions.Service.View,
	allPermissions.Discount.View,
	allPermissions.ContractLineItem.View,
	allPermissions.ContractLineItem.Create,
	allPermissions.ContractLineItem.Update,
	allPermissions.ContractLineItem.Delete,
	allPermissions.ContractInstallment.View,
	allPermissions.ContractInstallment.Create,
	allPermissions.ContractInstallment.Update,
	allPermissions.ContractInstallment.Delete,
];

function checkReEnrollmentPermissions(permissions: Record<string, boolean>) {
	for (let i = 0; i < requiredPermissionActions.length; i++) {
		const action = requiredPermissionActions[i];
		if (!(permissions[action.Admin] || permissions[action.Basic])) {
			return false;
		}
	}
	return true;
}

export type ReEnrollmentNoticeProps = Partial<ClassNameProps & StyleProps>;

export function ReEnrollmentNotice({ className, style }: ReEnrollmentNoticeProps) {
	const { loading, data } = useReEnrollmentNoticeQuery({
		variables: {
			studentFilters: getReEnrollmentStudentFilter(),
			contractFilters: getReEnrollmentContractInStudentFilter(),
		},
	});
	const studentIDs = data?.studentConnection.edges.map(({ node }) => node.id) ?? [];
	const anyIncomplete =
		data?.studentConnection.edges.some(({ node }) => node.contracts.some((e) => e.contractInstallments.length === 0)) ??
		true;

	const shouldNotRender = (loading && !data) || studentIDs.length === 0;

	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const { width } = useElementSize(containerRef, shouldNotRender);
	const isNarrow = width < 600;

	const history = useHistory();
	const onClick = React.useCallback(() => {
		history.push(reEnrollmentPageInfo.to);
	}, [history]);

	if (shouldNotRender) {
		return null;
	}

	return (
		<React.Fragment>
			<div ref={containerRef} onClick={onClick}>
				<Conditional>
					<If condition={anyIncomplete}>
						<Row
							justify="space-between"
							align="center"
							className={makeClassName(
								styles["re-enrollment-notice"],
								styles["re-enrollment-notice--incomplete"],
								isNarrow ? styles["re-enrollment-notice--narrow"] : "",
								className,
							)}
							style={{ rowGap: "1rem", ...style }}
						>
							<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
								<Column justify="spaced-start" verticalSpacing="0.5rem">
									<Row justify="space-between" align="flex-start" style={{ columnGap: "1rem" }}>
										<span className={styles["re-enrollment-notice__heading"]}>
											Re-Enrollment for 2024-2025 - Step 3!
										</span>
										<If condition={isNarrow}>
											<Icon.Star size={36} weight="fill" color="var(--brand-colors--primary--dark)" />
										</If>
									</Row>
									<span className={styles["re-enrollment-notice__message"]}>
										Please click the button below to complete the 3rd step of re-enrollment and secure your{" "}
										{studentIDs.length === 1 ? "student's" : "students'"} enrollment status for next&nbsp;year.
									</span>
								</Column>
								<Button variant="primary" onClick={doNothing}>
									Enroll Students
								</Button>
							</Column>

							<If condition={!isNarrow}>
								<Image
									src={StaticURLs.Images.schoolScenery1}
									alt="School Scenery"
									width={205}
									height={135}
									sizing="width-scaled"
									className={styles["re-enrollment-notice__image"]}
								/>
							</If>
						</Row>
					</If>
					<Else>
						<Row
							justify="space-between"
							align="center"
							className={makeClassName(
								styles["re-enrollment-notice"],
								styles["re-enrollment-notice--complete"],
								isNarrow ? styles["re-enrollment-notice--narrow"] : "",
								className,
							)}
							style={{ rowGap: "1rem", ...style }}
						>
							<Column justify="spaced-start" verticalSpacing="0.5rem" style={{ flexGrow: 1 }}>
								<Row justify="space-between" align="flex-start" style={{ columnGap: "1rem" }}>
									<span className={styles["re-enrollment-notice__heading"]}>
										Re-Enrollment 2024-2025 Step 3 Complete!
									</span>
									<If condition={isNarrow}>
										<Icon.CheckCircle size={24} color="var(--theme-colors__green)" />
									</If>
								</Row>
								<Button variant="link" size="small" onClick={doNothing}>
									Edit Re-Enrollment Details
								</Button>
							</Column>

							<If condition={!isNarrow}>
								<Icon.CheckCircle size={57} color="var(--theme-colors__green)" />
							</If>
						</Row>
					</Else>
				</Conditional>
			</div>
		</React.Fragment>
	);
}
